import { useRootStore } from 'store';
import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useApolloClient } from '@apollo/client';
import { FaFileImport, FaEye, FaFileDownload } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { gqlAdd, gqlUpdate } from '../../graphql';

import { ImportProgressModal } from './ImportProgressModal';
import { useImportProgress } from './useImportProgress';
import { PreImportModal } from './PreImportModal';
import {
  cellToString,
  dataFieldsForProcessing,
  ImportFieldMapState,
  mapHeaderToFields,
  MappedImportField,
  resetRowFieldValues,
  resolveCollectionpointName,
} from './import-column-mapper';
import { downloadCollectionpointImportTemplate } from './import-template';
import { getWorksheetRowValues, scanImportWorksheetRows } from './import-worksheet-scan';
import {
  annotateWorksheetWithResults,
  buildResultFileName,
  RowImportResult,
  workbookToResultBlob,
} from './import-result-annotator';
import {
  ensureCollectorCollectionpointLink,
  findCollectionpointsByName,
  loadImportLookupCache,
  parseNumber,
  parseYesNo,
  resolveCategoryId,
  resolveRegion,
  resolveSubregion,
  resolveWasteCategoryIds,
} from './import-lookups';

export const ImportExcel = ({ refetch }: { refetch?: () => void }) => {
  const client = useApolloClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const { appStore } = useRootStore();
  const { t } = useTranslation();

  const [importState, importAPI] = useImportProgress();
  const [showModal, setShowModal] = useState(false);
  const [showPreModal, setShowPreModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const openFileBrowser = () => {
    if (!fileInput.current) return;
    // Επαναφορά ώστε να πυροδοτείται το onChange και με το ίδιο αρχείο
    fileInput.current.value = '';
    fileInput.current.click();
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadCollectionpointImportTemplate();
    } catch (err) {
      console.error('Λήψη προτύπου εισαγωγής:', err);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleChange = async (e) => {
    if (!e.target.files.length) return;
    const file = e.target.files[0];
    setSelectedFile(file);
    setShowPreModal(true);
  };

  const handlePreImportSubmit = () => {
    setShowPreModal(false);
    setShowModal(true);
    if (selectedFile) {
      handleImport(selectedFile);
    }
    setSelectedFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  /** Συλλέκτης συνδέει το σημείο μόνο όταν ο χρήστης είναι collector */
  const collectorIdForLink =
    appStore.isCollector && appStore.associatedId ? appStore.associatedId : null;

  async function handleImport(target: File): Promise<void> {
    const Excel = await import('exceljs');
    const wb = new Excel.Workbook();
    const reader = new FileReader();

    reader.readAsArrayBuffer(target);

    reader.onload = () => {
      void (async () => {
        importAPI.start();
        importAPI.advanceTo('load');
        importAPI.log('info', 'Έναρξη εισαγωγής σημείων συλλογής');

        const buffer = reader.result as ArrayBuffer;
        importAPI.advanceTo('parse');

        try {
          const workbook = await wb.xlsx.load(buffer);

          // Το _worksheets είναι αραιός πίνακας με κλειδί το sheetId του Excel.
          // Το πρώτο φύλλο συχνά δεν είναι στο [1] (π.χ. id=2 μετά από διαγραφή φύλλου).
          const dataSheet: any =
            workbook.getWorksheet('Δεδομένα') ??
            workbook.worksheets.find((ws) => ws && ws.name !== 'Οδηγίες') ??
            workbook.worksheets[0];
          if (!dataSheet) {
            throw new Error('Το αρχείο Excel δεν περιέχει φύλλο εργασίας.');
          }

          for (const sheet of [dataSheet]) {
            importAPI.setWorksheetName(sheet?.name);
            importAPI.setStep('load', 'done');
            importAPI.setStep('parse', 'done');
            importAPI.advanceTo('header');
            importAPI.log('info', `Επεξεργασία φύλλου: ${sheet?.name || 'Άγνωστο'}`);

            const headerRowIdx = findHeaderRow(sheet);
            if (headerRowIdx == undefined) {
              const errorMsg = `Δεν βρέθηκε επικεφαλίδα στο φύλλο ${sheet.name}`;
              importAPI.log('error', errorMsg);
              importAPI.setStep('header', 'error');
              importAPI.incError();
              importAPI.stop();
              continue;
            }
            importAPI.log('info', `Βρέθηκε επικεφαλίδα στη γραμμή ${headerRowIdx + 1}`);

            importAPI.advanceTo('map');
            let fieldMapState: ImportFieldMapState;
            try {
              const headerRow = sheet._rows[headerRowIdx]._cells.map((v) => v._value.value);
              fieldMapState = mapHeaderToFields(headerRow);
              importAPI.log('info', 'Επιτυχής αντιστοίχιση στηλών');
            } catch (error) {
              const errorMsg = `Δεν βρέθηκαν όλα τα πεδία στην επικεφαλίδα του φύλλου "${
                sheet.name
              }" \n${(error as Error).message}`;
              importAPI.log('error', errorMsg);
              importAPI.setStep('map', 'error');
              importAPI.incError();
              importAPI.stop();
              continue;
            }

            importAPI.advanceTo('iterate');
            let lookupCache;
            try {
              lookupCache = await loadImportLookupCache(client);
              importAPI.log(
                'info',
                `Φορτώθηκαν lookups: ${lookupCache.categories.size} κατηγορίες, ${lookupCache.regions.size} περιφέρειες, ${lookupCache.subregions.size} Π.Ε., ${lookupCache.wasteCategories.size} κατηγορίες αποβλήτων.`,
              );
            } catch (lookupErr) {
              importAPI.log('error', `Αποτυχία φόρτωσης lookups: ${(lookupErr as Error).message}`);
              importAPI.setStep('validate', 'error');
              importAPI.incError();
              importAPI.stop();
              continue;
            }

            const rowScan = scanImportWorksheetRows(sheet, headerRowIdx, fieldMapState);
            let nullRowCount = 0;
            let rowsHandled = 0;
            const rowResults: RowImportResult[] = [];

            importAPI.log(
              'info',
              `Ξεκινάει επεξεργασία ${rowScan.totalRows} γραμμών δεδομένων (γραμμές ${
                rowScan.startRow + 1
              }–${rowScan.endRowInclusive + 1})`,
            );

            for (let j = rowScan.startRow; j <= rowScan.endRowInclusive; j++) {
              rowsHandled++;
              importAPI.setProgress((rowsHandled / rowScan.totalRows) * 100);

              const row = getWorksheetRowValues(sheet, j);
              if (!row) continue;

              if (nullRowCount >= 2) {
                importAPI.log('info', 'Τερματισμός λόγω 2 συνεχόμενων κενών γραμμών');
                importAPI.setTerminatedByEmptyRows(j);
                break;
              }

              importAPI.advanceTo('validate');
              resetRowFieldValues(fieldMapState);

              const name = resolveCollectionpointName(fieldMapState, row);
              if (name === null) {
                nullRowCount += 1;
                importAPI.log('info', `Κενή γραμμή (row=${j}) — skip`, j);
                continue;
              }
              nullRowCount = 0;

              let skipRow = false;
              let rowErrorMessage: string | undefined;

              for (const mapped of dataFieldsForProcessing(fieldMapState)) {
                if (skipRow) break;
                const value = row[mapped.colIdx];
                mapped.colValue = value;
                try {
                  mapped.fieldValue = resolveFieldValue(mapped, value, lookupCache);
                } catch (resolveErr) {
                  rowErrorMessage = (resolveErr as Error).message;
                  importAPI.log('error', `${rowErrorMessage} (row=${j})`, j);
                  importAPI.incError();
                  importAPI.setStep('validate', 'error');
                  skipRow = true;
                }
              }

              if (skipRow) {
                rowResults.push({
                  sheetRowIndex: j,
                  status: 'error',
                  message: rowErrorMessage || 'Σφάλμα επικύρωσης',
                });
                continue;
              }

              const geoErr = validateRegionSubregion(fieldMapState);
              if (geoErr) {
                importAPI.log('error', `${geoErr} (row=${j})`, j);
                importAPI.incError();
                importAPI.setStep('validate', 'error');
                rowResults.push({ sheetRowIndex: j, status: 'error', message: geoErr });
                continue;
              }

              importAPI.advanceTo('check');
              let existing: { id: number; name: string }[] = [];
              try {
                existing = await findCollectionpointsByName(client, name);
              } catch (findErr) {
                const msg = `Αποτυχία αναζήτησης επωνυμίας: ${(findErr as Error).message}`;
                importAPI.log('error', `${msg} (row=${j})`, j);
                importAPI.incError();
                rowResults.push({ sheetRowIndex: j, status: 'error', message: msg });
                continue;
              }

              if (existing.length > 1) {
                const msg = `Βρέθηκαν ${existing.length} σημεία με επωνυμία "${name}". Η ενημέρωση ακυρώθηκε.`;
                importAPI.log('error', `${msg} (row=${j})`, j);
                importAPI.incError();
                importAPI.setStep('check', 'error');
                rowResults.push({ sheetRowIndex: j, status: 'error', message: msg });
                continue;
              }

              const isUpdate = existing.length === 1;
              const payload = buildMutationPayload(fieldMapState);

              if (!isUpdate) {
                const insertErr = validateRequiredForInsert(payload);
                if (insertErr) {
                  importAPI.log('error', `${insertErr} (row=${j})`, j);
                  importAPI.incError();
                  importAPI.setStep('validate', 'error');
                  rowResults.push({ sheetRowIndex: j, status: 'error', message: insertErr });
                  continue;
                }
                if (payload.isActive === undefined) payload.isActive = true;
              }

              importAPI.advanceTo('insert');
              try {
                let savedId: number | undefined;
                if (isUpdate) {
                  const existingId = existing[0].id;
                  const mutationResult = await client.mutate({
                    mutation: gqlUpdate,
                    variables: { data: { ...payload, id: existingId } },
                  });
                  savedId = mutationResult?.data?.updateCollectionpoint?.id ?? existingId;
                  importAPI.log('info', `Ενημερώθηκε το σημείο "${name}" (ID=${savedId})`, j);
                  importAPI.incUpdated();
                } else {
                  const mutationResult = await client.mutate({
                    mutation: gqlAdd,
                    variables: { data: payload },
                  });
                  savedId = mutationResult?.data?.addCollectionpoint?.id;
                  importAPI.log(
                    'info',
                    `Καταχωρήθηκε νέο σημείο "${name}"${savedId != null ? ` (ID=${savedId})` : ''}`,
                    j,
                  );
                  importAPI.incInserted();
                }

                if (savedId != null && collectorIdForLink) {
                  try {
                    const link = await ensureCollectorCollectionpointLink(
                      client,
                      collectorIdForLink,
                      savedId,
                    );
                    if (link === 'created') {
                      importAPI.log(
                        'info',
                        `Συνδέθηκε το σημείο ID=${savedId} με τον συλλέκτη ${collectorIdForLink}.`,
                        j,
                      );
                    }
                  } catch (linkErr) {
                    importAPI.log(
                      'warn',
                      `Το σημείο αποθηκεύτηκε αλλά απέτυχε η σύνδεση συλλέκτη: ${
                        (linkErr as Error).message
                      }`,
                      j,
                    );
                  }
                }

                importAPI.incProcessed();
                rowResults.push({
                  sheetRowIndex: j,
                  status: isUpdate ? 'updated' : 'success',
                  collectionPointId: savedId,
                  message: isUpdate
                    ? `Ενημερώθηκε (ID ${savedId})`
                    : savedId != null
                    ? `Καταχωρήθηκε με ID ${savedId}`
                    : 'Καταχωρήθηκε επιτυχώς',
                });
              } catch (error) {
                const msg = `Σφάλμα καταχώρισης: ${(error as Error).message}`;
                importAPI.log('error', `${msg} (row=${j})`, j);
                importAPI.incError();
                importAPI.setStep('insert', 'error');
                rowResults.push({ sheetRowIndex: j, status: 'error', message: msg });
              }
            }

            if (rowResults.length > 0) {
              try {
                annotateWorksheetWithResults(sheet, headerRowIdx, rowResults);
                const resultBlob = await workbookToResultBlob(workbook);
                const resultFileName = buildResultFileName(target.name);
                importAPI.setResultFile({ blob: resultBlob, fileName: resultFileName });
                importAPI.log(
                  'info',
                  `Έτοιμο αρχείο αποτελεσμάτων (${rowResults.length} γραμμές με κατάσταση).`,
                );
              } catch (annotateErr) {
                importAPI.log(
                  'warn',
                  `Δεν ήταν δυνατή η δημιουργία αρχείου αποτελεσμάτων: ${
                    (annotateErr as Error).message
                  }`,
                );
              }
            }

            importAPI.setProgress(100);
            importAPI.setStep('insert', 'done');
            importAPI.stop();

            const s = importAPI.getState();
            const reasons: string[] = [];
            if (s.terminatedByEmptyRows) {
              reasons.push(
                `Τερματισμός λόγω 2 συνεχόμενων κενών γραμμών (στη γραμμή ${s.terminatedByEmptyRows.atRow}).`,
              );
            }
            if (s.inserted === 0 && s.updated === 0 && s.errors > 0) {
              reasons.push('Καμία έγκυρη γραμμή δεν καταχωρήθηκε ή ενημερώθηκε.');
            }

            const saved = s.inserted + s.updated;
            const status = saved === 0 ? 'empty' : s.errors > 0 ? 'partial' : 'success';

            importAPI.setOutcome({ status, reasons });
            importAPI.log(
              'info',
              `Ολοκληρώθηκε η εισαγωγή. Επεξεργάστηκαν: ${s.processed}, Νέα: ${s.inserted}, Ενημερώσεις: ${s.updated}, Σφάλματα: ${s.errors}`,
              undefined,
              { stepKey: 'insert' },
            );

            if (typeof refetch === 'function') refetch();
          }
        } catch (loadErr) {
          importAPI.log('error', `Αποτυχία ανάγνωσης αρχείου: ${(loadErr as Error).message}`);
          importAPI.setStep('parse', 'error');
          importAPI.incError();
          importAPI.stop();
          importAPI.setOutcome({
            status: 'empty',
            reasons: ['Το αρχείο Excel δεν ήταν δυνατό να διαβαστεί.'],
          });
        }
      })();
    };
  }

  if (!(appStore.isPlatformOwner || appStore.isCollector)) return <></>;
  return (
    <>
      <span className="d-inline-flex align-items-center">
        <Button
          variant="outline-secondary"
          size="sm"
          className="text-nowrap me-2"
          style={{ zIndex: 0 }}
          onClick={handleDownloadTemplate}
          disabled={importState.running || downloadingTemplate}
          title={t('Λήψη προτύπου Excel')}
        >
          <FaFileDownload className="mb-1 me-1 flex-shrink-0" />
          {downloadingTemplate ? t('Λήψη…') : t('Πρότυπο Εισαγωγής')}
        </Button>

        <Button
          variant="info"
          size="sm"
          className="text-nowrap"
          style={{ minWidth: '185px', zIndex: 0 }}
          onClick={openFileBrowser}
          disabled={importState.running}
        >
          <FaFileImport className="mb-1 me-2 flex-shrink-0" />
          {t('Εισαγωγή από Excel')}
        </Button>

        {(importState.running || importState.logs.length > 0) && (
          <Button
            variant="outline-secondary"
            size="sm"
            className="ms-2 d-inline-flex align-items-center shadow-sm"
            style={{ zIndex: 0 }}
            onClick={() => setShowModal(true)}
            title="Προβολή προόδου εισαγωγής"
          >
            <FaEye className="me-1" /> {importState.running ? 'Σε εξέλιξη…' : 'Προβολή Εισαγωγής'}
          </Button>
        )}

        <input hidden type="file" onChange={handleChange} accept=".xlsx" ref={fileInput} />
      </span>

      <PreImportModal
        show={showPreModal}
        onClose={() => {
          setShowPreModal(false);
          setSelectedFile(null);
          if (fileInput.current) fileInput.current.value = '';
        }}
        onSubmit={handlePreImportSubmit}
        onDownloadTemplate={handleDownloadTemplate}
      />

      <ImportProgressModal
        show={showModal}
        onClose={() => setShowModal(false)}
        state={importState}
      />
    </>
  );
};

/** Πρώτη μη κενή γραμμή του φύλλου — εκεί περιμένουμε την επικεφαλίδα */
function findHeaderRow(sheet) {
  const rows = sheet?._rows;
  if (!rows?.length) return undefined;
  let headerRowIdx: number = undefined;
  for (let j = 0; j < rows.length; j++) {
    if (rows[j] !== undefined) {
      headerRowIdx = j;
      break;
    }
  }
  return headerRowIdx;
}

/** Μετατροπή κελιού σύμφωνα με τον resolver του πεδίου */
function resolveFieldValue(
  mapped: MappedImportField,
  value: unknown,
  lookupCache: Awaited<ReturnType<typeof loadImportLookupCache>>,
): unknown {
  const fieldName = mapped.def.field;
  const resolver = mapped.def.resolver ?? 'text';
  const text = cellToString(value);

  if (resolver === 'yesno') {
    return parseYesNo(value);
  }
  if (resolver === 'number') {
    return parseNumber(value);
  }
  if (resolver === 'categoryName') {
    if (!text) return undefined;
    const id = resolveCategoryId(lookupCache, value);
    if (!id) throw new Error(`Δεν βρέθηκε η Κατηγορία "${text}".`);
    return id;
  }
  if (resolver === 'regionName') {
    if (!text) return undefined;
    const rec = resolveRegion(lookupCache, value);
    if (!rec) throw new Error(`Δεν βρέθηκε η Περιφέρεια "${text}".`);
    return rec.id;
  }
  if (resolver === 'subregionName') {
    if (!text) return undefined;
    const rec = resolveSubregion(lookupCache, value);
    if (!rec) throw new Error(`Δεν βρέθηκε η Περιφερειακή Ενότητα "${text}".`);
    return rec;
  }
  if (resolver === 'wasteCategoryNames') {
    if (!text) return undefined;
    const { ids, missing } = resolveWasteCategoryIds(lookupCache, value);
    if (missing.length) {
      throw new Error(`Δεν βρέθηκαν κατηγορίες αποβλήτων: ${missing.join(', ')}`);
    }
    return ids.join(',');
  }

  if (fieldName === 'name') return text;
  return text;
}

/** Αν δόθηκαν και Π.Ε. και περιφέρεια, πρέπει να συμφωνούν */
function validateRegionSubregion(state: ImportFieldMapState): string | undefined {
  const sub = state.fields.subregionId?.fieldValue as { id: number; regionId?: number } | undefined;
  const regionId = state.fields.regionId?.fieldValue as number | undefined;
  if (sub && regionId && sub.regionId && sub.regionId !== regionId) {
    return 'Η Περιφερειακή Ενότητα δεν ανήκει στην επιλεγμένη Περιφέρεια.';
  }
  return undefined;
}

/** Payload GraphQL: κενά κελιά παραλείπονται ώστε το update να μην σβήνει τιμές */
function buildMutationPayload(state: ImportFieldMapState): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  for (const mapped of dataFieldsForProcessing(state)) {
    const field = mapped.def.field;
    let value = mapped.fieldValue;
    if (value === undefined || value === null || value === '') continue;

    if (field === 'subregionId' && typeof value === 'object') {
      const sub = value as { id: number; regionId?: number };
      payload.subregionId = sub.id;
      // Αν λείπει η περιφέρεια από το Excel, συμπληρώνεται από την Π.Ε.
      if (payload.regionId == null && sub.regionId) payload.regionId = sub.regionId;
      continue;
    }

    payload[field] = value;
  }

  return payload;
}

function validateRequiredForInsert(payload: Record<string, unknown>): string | undefined {
  if (!payload.name) return 'Λείπει η επωνυμία.';
  if (!payload.addressLine1) return 'Για νέα καταχώριση απαιτείται Διεύθυνση.';
  if (!payload.phone) return 'Για νέα καταχώριση απαιτείται Τηλέφωνο.';
  return undefined;
}
